"use client";

import React, { useState } from "react";
import LegalTopBar from "@/components/demos/legal/LegalTopBar";
import LegalHero from "@/components/demos/legal/LegalHero";
import PracticeAreasSection from "@/components/demos/legal/PracticeAreasSection";
import LegalTrustSection from "@/components/demos/legal/LegalTrustSection";
import BciDisclaimerModal from "@/components/demos/legal/BciDisclaimerModal";
import CaseEvaluationModal from "@/components/demos/legal/CaseEvaluationModal";
import CnrTrackingModal from "@/components/demos/legal/CnrTrackingModal";
import { PracticeAreaInfo } from "@/lib/demos/legalData";
import { Scale, Lock, ShieldCheck } from "lucide-react";
import Link from "next/link";

export default function LegalLandingPage() {
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [isCnrModalOpen, setIsCnrModalOpen] = useState(false);
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<PracticeAreaInfo | null>(null);

  const handleSelectPracticeArea = (area: PracticeAreaInfo) => {
    setSelectedPracticeArea(area);
    setIsEvaluationModalOpen(true);
  };

  const handleOpenGeneralEvaluation = () => {
    setSelectedPracticeArea(null);
    setIsEvaluationModalOpen(true);
  };

  return (
    <main className="min-h-screen flex flex-col bg-slate-950 text-slate-100">
      {/* Mandatory Bar Council of India Disclaimer Gate */}
      <BciDisclaimerModal />

      {/* Navigation Top Bar */}
      <LegalTopBar
        activeTab="portal"
        currency={currency}
        onCurrencyToggle={setCurrency}
        onOpenCnrModal={() => setIsCnrModalOpen(true)}
        onOpenEvaluationModal={handleOpenGeneralEvaluation}
      />

      {/* Hero with Cause List Preview */}
      <LegalHero
        currency={currency}
        onOpenEvaluationModal={handleOpenGeneralEvaluation}
        onOpenCnrModal={() => setIsCnrModalOpen(true)}
      />

      {/* Factual Practice Directory */}
      <PracticeAreasSection
        currency={currency}
        onSelectPracticeArea={handleSelectPracticeArea}
      />

      {/* Statutory Trust & Ethics Section */}
      <LegalTrustSection />

      {/* Chambers Footer */}
      <footer className="py-12 bg-slate-950 border-t border-slate-850 text-slate-400 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center">
                <Scale className="w-4 h-4" />
              </div>
              <span className="font-serif font-bold text-white text-base">
                LexJuris Associates & Partners
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-slate-500">
              <span>Chambers: High Court Enclave, Sher Shah Road, New Delhi</span>
              <span>•</span>
              <span>Bar Council of Delhi Reg. 2026</span>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] leading-relaxed text-slate-400">
            <p className="font-semibold text-slate-300 mb-1">Regulatory & Statutory Disclaimer:</p>
            <p>
              This website is an interactive software demonstration developed by Johnson Softwares to exhibit custom Legal Practice Management, CNR tracking, and Attorney CRM automation capabilities. In compliance with Rule 36 of the Bar Council of India Rules, no advocate has solicited or advertised through this portal.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-500 pt-4 border-t border-slate-850">
            <span>© 2026 LexJuris Chambers • Developed by Johnson Softwares</span>
            <div className="flex items-center gap-4">
              <Link href="/demos/legal/crm" className="text-amber-400 hover:text-amber-300 font-semibold">
                Attorney CRM Portal →
              </Link>
              <Link href="/demos" className="text-slate-400 hover:text-white">
                All Industry Demos
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Case Evaluation & Retainer Intake Modal */}
      <CaseEvaluationModal
        isOpen={isEvaluationModalOpen}
        onClose={() => setIsEvaluationModalOpen(false)}
        currency={currency}
        initialPracticeArea={selectedPracticeArea}
      />

      {/* CNR Status Tracking Modal */}
      <CnrTrackingModal
        isOpen={isCnrModalOpen}
        onClose={() => setIsCnrModalOpen(false)}
      />
    </main>
  );
}
